// (c) chaehongee. Confidential and proprietary.

import type { ReactNode, ReactElement } from 'react'
import { useEffect, useRef } from 'react'
import * as Sentry from '@sentry/react'
import styles from './Layout.module.css'
import { StatusBar } from '../StatusBar/StatusBar'
import { useThreadCanvasResizer } from './hooks/useThreadCanvasResizer'
import { useAppSelector } from '../../store/hooks/hooks'
import { BrandedLoader } from './BrandedLoader'
import { ReadOnlyBanner } from '../Auth/ReadOnlyBanner'
import { LayoutErrorFallback } from '../ErrorFallback'
import { selectIsActiveSessionLoading, selectActiveSessionLoadingMessage } from '../../store/selectors/sessionSelectors'

export interface LayoutProps {
    treePanel: ReactNode
    canvasPanel: ReactNode
    threadPanel: ReactNode
}

export function Layout({ treePanel, canvasPanel, threadPanel }: LayoutProps): ReactElement {
    const rootRef = useRef<HTMLDivElement | null>(null)
    const isLoading = useAppSelector(selectIsActiveSessionLoading)
    const loadingMessage = useAppSelector(selectActiveSessionLoadingMessage)
    const hasEverCompletedLoadRef = useRef(false)
    useEffect(() => {
        if (!isLoading) {
            hasEverCompletedLoadRef.current = true
        }
    }, [isLoading])

    const showFullScreenLoader = isLoading && !hasEverCompletedLoadRef.current
    const {
        cssVars,
        isDragging,
        isCollapsed,
        handlePointerDown,
        handleDoubleClick,
    } = useThreadCanvasResizer(rootRef)

    return (
        <Sentry.ErrorBoundary fallback={<LayoutErrorFallback />}>
        <div
            ref={rootRef}
            className={`${styles.root} ${isDragging ? styles.dragging : ''} ${isCollapsed ? styles.collapsed : ''}`}
            style={cssVars as React.CSSProperties}
        >
            <div className={styles.statusbar}><StatusBar /></div>
            <div className={styles.thread}>
                {threadPanel}
            </div>
            <div className={styles.tree}>
                {treePanel}
            </div>
            <div className={styles.canvas}>
                {canvasPanel}
            </div>
            <div
                className={styles.resizer}
                onPointerDown={handlePointerDown}
                onDoubleClick={handleDoubleClick}
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize thread panel"
            />
            {isLoading && (
                showFullScreenLoader ? (
                    <BrandedLoader message={loadingMessage} />
                ) : (
                    <div className={styles.contentLoadingOverlay}>
                        <BrandedLoader message={loadingMessage} />
                    </div>
                )
            )}
            {!isLoading && <ReadOnlyBanner />}
        </div>
        </Sentry.ErrorBoundary>
    )
}


